import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { SectionNav } from "@/components/commerce/section-nav";
import { DeliveryService } from "@/gen/omnios/v1/delivery_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Globe,
	Loader2,
	Pencil,
	Plus,
	Ruler,
	Trash2,
	Truck,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/commerce/delivery")({
	component: DeliveryPage,
});

const deliveryClient = createClient(DeliveryService, transport);

type Section = "methods" | "zones" | "rates";

function DeliveryPage() {
	const { t } = useTranslation("commerce");

	const NAV_ITEMS: {
		value: Section;
		label: string;
		icon: typeof Truck;
	}[] = [
		{ value: "methods", label: t("del_nav_methods"), icon: Truck },
		{ value: "zones", label: t("del_nav_zones"), icon: Globe },
		{ value: "rates", label: t("del_nav_rates"), icon: Ruler },
	];

	useBreadcrumbs([{ label: t("delivery") }]);
	const [section, setSection] = useState<Section>("methods");

	return (
		<div className="flex h-full overflow-hidden">
			<SectionNav items={NAV_ITEMS} value={section} onChange={setSection} />

			<div className="flex-1 overflow-y-auto p-6">
				{section === "methods" && <DeliveryMethodsPanel />}
				{section === "zones" && <DeliveryZonesPanel />}
				{section === "rates" && <DeliveryRatesPanel />}
			</div>
		</div>
	);
}

// -- Delivery Methods ----------------------------------------

interface MethodForm {
	displayName: string;
	providerSlug: string;
	supportedCountries: string;
	configJson: string;
	sortOrder: number;
}

const emptyMethodForm: MethodForm = {
	displayName: "",
	providerSlug: "cdek",
	supportedCountries: "RU",
	configJson: "{}",
	sortOrder: 0,
};

function DeliveryMethodsPanel() {
	const { t } = useTranslation("commerce");
	const qc = useQueryClient();
	const [showDialog, setShowDialog] = useState(false);
	const [editId, setEditId] = useState<string | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [form, setForm] = useState<MethodForm>(emptyMethodForm);

	const { data, isLoading } = useQuery({
		queryKey: ["delivery-methods"],
		queryFn: () => deliveryClient.listDeliveryMethods({ activeOnly: false }),
	});

	const createMut = useMutation({
		mutationFn: () => {
			let config: Record<string, unknown> | undefined;
			try {
				config = JSON.parse(form.configJson);
			} catch {
				config = {};
			}
			return deliveryClient.configureDeliveryMethod({
				displayName: form.displayName,
				providerSlug: form.providerSlug,
				supportedCountries: form.supportedCountries
					.split(",")
					.map((s) => s.trim())
					.filter(Boolean),
				config: config as any,
				sortOrder: form.sortOrder,
			});
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["delivery-methods"] });
			closeDialog();
		},
	});

	const updateMut = useMutation({
		mutationFn: () => {
			let config: Record<string, unknown> | undefined;
			try {
				config = JSON.parse(form.configJson);
			} catch {
				config = {};
			}
			return deliveryClient.updateDeliveryMethod({
				id: editId!,
				displayName: form.displayName,
				config: config as any,
				sortOrder: form.sortOrder,
			});
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["delivery-methods"] });
			closeDialog();
		},
	});

	const toggleActiveMut = useMutation({
		mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
			deliveryClient.updateDeliveryMethod({ id, isActive }),
		onSuccess: () =>
			qc.invalidateQueries({ queryKey: ["delivery-methods"] }),
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) =>
			deliveryClient.deleteDeliveryMethod({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["delivery-methods"] });
			setDeleteTarget(null);
		},
	});

	function openCreate() {
		setEditId(null);
		setForm(emptyMethodForm);
		setShowDialog(true);
	}

	function openEdit(m: any) {
		setEditId(m.id);
		setForm({
			displayName: m.displayName,
			providerSlug: m.providerSlug,
			supportedCountries: (m.supportedCountries ?? []).join(", "),
			configJson: "{}",
			sortOrder: m.sortOrder ?? 0,
		});
		setShowDialog(true);
	}

	function closeDialog() {
		setShowDialog(false);
		setEditId(null);
		setForm(emptyMethodForm);
	}

	const methods = data?.methods ?? [];

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-medium">{t("del_methods_title")}</h3>
				<Button size="sm" onClick={openCreate}>
					<Plus className="h-3.5 w-3.5 mr-1.5" />
					{t("common:create")}
				</Button>
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : methods.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					{t("del_methods_empty")}
				</p>
			) : (
				<div className="rounded-lg border overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="border-b bg-muted/30">
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
									{t("del_col_name")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground w-32">
									{t("del_col_provider")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground w-20">
									{t("del_col_active")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
									{t("del_col_countries")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground w-20">
									{t("del_col_sort")}
								</TableHead>
								<TableHead className="px-3 py-2 w-20" />
							</TableRow>
						</TableHeader>
						<TableBody className="divide-y">
							{methods.map((m) => (
								<TableRow key={m.id} className="hover:bg-muted/50">
									<TableCell className="px-3 py-2 font-medium">{m.displayName}</TableCell>
									<TableCell className="px-3 py-2 text-muted-foreground">
										{m.providerSlug}
									</TableCell>
									<TableCell className="px-3 py-2">
										<Switch
											checked={m.isActive}
											onCheckedChange={(v) =>
												toggleActiveMut.mutate({ id: m.id, isActive: v })
											}
										/>
									</TableCell>
									<TableCell className="px-3 py-2 text-xs text-muted-foreground">
										{m.supportedCountries.join(", ") || "--"}
									</TableCell>
									<TableCell className="px-3 py-2 tabular-nums text-muted-foreground">
										{m.sortOrder}
									</TableCell>
									<TableCell className="px-3 py-2 text-right flex gap-1 justify-end">
										<Button
											variant="ghost"
											size="sm"
											className="h-7 w-7 p-0 text-muted-foreground"
											onClick={() => openEdit(m)}
										>
											<Pencil className="h-3.5 w-3.5" />
										</Button>
										<Button
											variant="ghost"
											size="sm"
											className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
											onClick={() => setDeleteTarget(m.id)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			{/* Create / Edit Dialog */}
			<Dialog open={showDialog} onOpenChange={(o) => !o && closeDialog()}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{editId ? t("del_edit_method") : t("del_new_method")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("del_col_name")}</Label>
							<Input
								value={form.displayName}
								onChange={(e) =>
									setForm({ ...form, displayName: e.target.value })
								}
								placeholder={t("del_name_placeholder")}
							/>
						</div>
						{!editId && (
							<div className="space-y-2">
								<Label>{t("del_col_provider")}</Label>
								<Select
									value={form.providerSlug}
									onValueChange={(v) =>
										setForm({ ...form, providerSlug: v })
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="cdek">CDEK</SelectItem>
										<SelectItem value="yandex_delivery">
											Яндекс Доставка
										</SelectItem>
										<SelectItem value="manual">
											{t("del_provider_manual")}
										</SelectItem>
										<SelectItem value="pickup">
											{t("del_provider_pickup")}
										</SelectItem>
										<SelectItem value="flat_rate">
											{t("del_provider_flat")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						)}
						{!editId && (
							<div className="space-y-2">
								<Label>{t("del_col_countries")}</Label>
								<Input
									value={form.supportedCountries}
									onChange={(e) =>
										setForm({
											...form,
											supportedCountries: e.target.value,
										})
									}
									placeholder="RU, KZ, BY"
								/>
							</div>
						)}
						<div className="space-y-2">
							<Label>{t("del_config_json")}</Label>
							<Textarea
								value={form.configJson}
								onChange={(e) =>
									setForm({ ...form, configJson: e.target.value })
								}
								rows={4}
								className="font-mono text-xs"
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("del_col_sort")}</Label>
							<Input
								type="number"
								value={form.sortOrder}
								onChange={(e) =>
									setForm({ ...form, sortOrder: Number(e.target.value) })
								}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeDialog}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() =>
								editId ? updateMut.mutate() : createMut.mutate()
							}
							disabled={
								!form.displayName ||
								createMut.isPending ||
								updateMut.isPending
							}
						>
							{(createMut.isPending || updateMut.isPending) && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{editId ? t("common:save") : t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Delete Confirm */}
			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("del_delete_method")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("del_action_irreversible")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteTarget && deleteMut.mutate(deleteTarget)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

// -- Delivery Zones ----------------------------------------

interface ZoneForm {
	name: string;
	countries: string;
	regions: string;
	postalCodes: string;
}

const emptyZoneForm: ZoneForm = {
	name: "",
	countries: "",
	regions: "",
	postalCodes: "",
};

function DeliveryZonesPanel() {
	const { t } = useTranslation("commerce");
	const qc = useQueryClient();
	const [showDialog, setShowDialog] = useState(false);
	const [editId, setEditId] = useState<string | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [form, setForm] = useState<ZoneForm>(emptyZoneForm);

	const { data, isLoading } = useQuery({
		queryKey: ["delivery-zones"],
		queryFn: () => deliveryClient.listDeliveryZones({}),
	});

	const splitCsv = (s: string) =>
		s
			.split(",")
			.map((x) => x.trim())
			.filter(Boolean);

	const createMut = useMutation({
		mutationFn: () =>
			deliveryClient.createDeliveryZone({
				name: form.name,
				countries: splitCsv(form.countries),
				regions: splitCsv(form.regions),
				postalCodes: splitCsv(form.postalCodes),
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["delivery-zones"] });
			closeDialog();
		},
	});

	const updateMut = useMutation({
		mutationFn: () =>
			deliveryClient.updateDeliveryZone({
				id: editId!,
				name: form.name,
				countries: splitCsv(form.countries),
				regions: splitCsv(form.regions),
				postalCodes: splitCsv(form.postalCodes),
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["delivery-zones"] });
			closeDialog();
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => deliveryClient.deleteDeliveryZone({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["delivery-zones"] });
			setDeleteTarget(null);
		},
	});

	function openCreate() {
		setEditId(null);
		setForm(emptyZoneForm);
		setShowDialog(true);
	}

	function openEdit(z: any) {
		setEditId(z.id);
		setForm({
			name: z.name,
			countries: (z.countries ?? []).join(", "),
			regions: (z.regions ?? []).join(", "),
			postalCodes: (z.postalCodes ?? []).join(", "),
		});
		setShowDialog(true);
	}

	function closeDialog() {
		setShowDialog(false);
		setEditId(null);
		setForm(emptyZoneForm);
	}

	const zones = data?.zones ?? [];

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-medium">{t("del_zones_title")}</h3>
				<Button size="sm" onClick={openCreate}>
					<Plus className="h-3.5 w-3.5 mr-1.5" />
					{t("common:create")}
				</Button>
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : zones.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					{t("del_zones_empty")}
				</p>
			) : (
				<div className="rounded-lg border overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="border-b bg-muted/30">
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
									{t("del_col_zone_name")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
									{t("del_col_countries")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground w-28">
									{t("del_col_regions")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground w-28">
									{t("del_col_postals")}
								</TableHead>
								<TableHead className="px-3 py-2 w-20" />
							</TableRow>
						</TableHeader>
						<TableBody className="divide-y">
							{zones.map((z) => (
								<TableRow key={z.id} className="hover:bg-muted/50">
									<TableCell className="px-3 py-2 font-medium">{z.name}</TableCell>
									<TableCell className="px-3 py-2 text-xs text-muted-foreground">
										{z.countries.join(", ") || "--"}
									</TableCell>
									<TableCell className="px-3 py-2 tabular-nums text-muted-foreground">
										{z.regions.length}
									</TableCell>
									<TableCell className="px-3 py-2 tabular-nums text-muted-foreground">
										{z.postalCodes.length}
									</TableCell>
									<TableCell className="px-3 py-2 text-right flex gap-1 justify-end">
										<Button
											variant="ghost"
											size="sm"
											className="h-7 w-7 p-0 text-muted-foreground"
											onClick={() => openEdit(z)}
										>
											<Pencil className="h-3.5 w-3.5" />
										</Button>
										<Button
											variant="ghost"
											size="sm"
											className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
											onClick={() => setDeleteTarget(z.id)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			{/* Create / Edit Dialog */}
			<Dialog open={showDialog} onOpenChange={(o) => !o && closeDialog()}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{editId ? t("del_edit_zone") : t("del_new_zone")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("del_col_zone_name")}</Label>
							<Input
								value={form.name}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
								placeholder={t("del_zone_name_placeholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("del_col_countries")}</Label>
							<Input
								value={form.countries}
								onChange={(e) =>
									setForm({ ...form, countries: e.target.value })
								}
								placeholder="RU, KZ, BY"
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("del_col_regions")}</Label>
							<Input
								value={form.regions}
								onChange={(e) =>
									setForm({ ...form, regions: e.target.value })
								}
								placeholder={t("del_regions_placeholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("del_col_postals")}</Label>
							<Input
								value={form.postalCodes}
								onChange={(e) =>
									setForm({ ...form, postalCodes: e.target.value })
								}
								placeholder="101000, 101001"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeDialog}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() =>
								editId ? updateMut.mutate() : createMut.mutate()
							}
							disabled={
								!form.name ||
								createMut.isPending ||
								updateMut.isPending
							}
						>
							{(createMut.isPending || updateMut.isPending) && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{editId ? t("common:save") : t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Delete Confirm */}
			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("del_delete_zone")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("del_action_irreversible")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteTarget && deleteMut.mutate(deleteTarget)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

// -- Delivery Rates ----------------------------------------

interface RateForm {
	zoneId: string;
	methodName: string;
	priceCents: number;
	currency: string;
	minOrderCents: number;
	maxWeightG: number;
	freeAboveCents: number;
}

const emptyRateForm: RateForm = {
	zoneId: "",
	methodName: "",
	priceCents: 0,
	currency: "RUB",
	minOrderCents: 0,
	maxWeightG: 0,
	freeAboveCents: 0,
};

function DeliveryRatesPanel() {
	const { t } = useTranslation("commerce");
	const qc = useQueryClient();
	const [selectedZoneId, setSelectedZoneId] = useState<string>("");
	const [showDialog, setShowDialog] = useState(false);
	const [editId, setEditId] = useState<string | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [form, setForm] = useState<RateForm>(emptyRateForm);

	const { data: zonesData } = useQuery({
		queryKey: ["delivery-zones"],
		queryFn: () => deliveryClient.listDeliveryZones({}),
	});

	const zones = zonesData?.zones ?? [];

	const { data: ratesData, isLoading } = useQuery({
		queryKey: ["delivery-rates", selectedZoneId],
		queryFn: () =>
			deliveryClient.listDeliveryRates({ zoneId: selectedZoneId }),
		enabled: !!selectedZoneId,
	});

	const createMut = useMutation({
		mutationFn: () =>
			deliveryClient.createDeliveryRate({
				zoneId: form.zoneId,
				methodName: form.methodName,
				priceCents: BigInt(form.priceCents),
				currency: form.currency,
				minOrderCents: BigInt(form.minOrderCents),
				maxWeightG: form.maxWeightG,
				freeAboveCents: BigInt(form.freeAboveCents),
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["delivery-rates"] });
			closeDialog();
		},
	});

	const updateMut = useMutation({
		mutationFn: () =>
			deliveryClient.updateDeliveryRate({
				id: editId!,
				priceCents: BigInt(form.priceCents),
				minOrderCents: BigInt(form.minOrderCents),
				maxWeightG: form.maxWeightG,
				freeAboveCents: BigInt(form.freeAboveCents),
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["delivery-rates"] });
			closeDialog();
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => deliveryClient.deleteDeliveryRate({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["delivery-rates"] });
			setDeleteTarget(null);
		},
	});

	function openCreate() {
		setEditId(null);
		setForm({ ...emptyRateForm, zoneId: selectedZoneId });
		setShowDialog(true);
	}

	function openEdit(r: any) {
		setEditId(r.id);
		setForm({
			zoneId: r.zoneId,
			methodName: r.methodName,
			priceCents: Number(r.priceCents),
			currency: r.currency,
			minOrderCents: Number(r.minOrderCents),
			maxWeightG: r.maxWeightG,
			freeAboveCents: Number(r.freeAboveCents),
		});
		setShowDialog(true);
	}

	function closeDialog() {
		setShowDialog(false);
		setEditId(null);
		setForm(emptyRateForm);
	}

	const rates = ratesData?.rates ?? [];
	const zoneNameById = (id: string) =>
		zones.find((z) => z.id === id)?.name ?? id.slice(0, 8);

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-4">
				<h3 className="text-sm font-medium">{t("del_rates_title")}</h3>
				<Select value={selectedZoneId} onValueChange={setSelectedZoneId}>
					<SelectTrigger className="w-56">
						<SelectValue placeholder={t("del_select_zone")} />
					</SelectTrigger>
					<SelectContent>
						{zones.map((z) => (
							<SelectItem key={z.id} value={z.id}>
								{z.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{selectedZoneId && (
					<Button size="sm" onClick={openCreate} className="ml-auto">
						<Plus className="h-3.5 w-3.5 mr-1.5" />
						{t("common:create")}
					</Button>
				)}
			</div>

			{!selectedZoneId ? (
				<p className="text-sm text-muted-foreground py-8 text-center">
					{t("del_select_zone_hint")}
				</p>
			) : isLoading ? (
				<div className="flex items-center justify-center py-8">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : rates.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					{t("del_rates_empty")}
				</p>
			) : (
				<div className="rounded-lg border overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="border-b bg-muted/30">
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
									{t("del_col_zone_name")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
									{t("del_col_method_name")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground w-24">
									{t("del_col_price")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground w-28">
									{t("del_col_min_order")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground w-28">
									{t("del_col_max_weight")}
								</TableHead>
								<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground w-28">
									{t("del_col_free_above")}
								</TableHead>
								<TableHead className="px-3 py-2 w-20" />
							</TableRow>
						</TableHeader>
						<TableBody className="divide-y">
							{rates.map((r) => (
								<TableRow key={r.id} className="hover:bg-muted/50">
									<TableCell className="px-3 py-2 text-muted-foreground">
										{zoneNameById(r.zoneId)}
									</TableCell>
									<TableCell className="px-3 py-2 font-medium">{r.methodName}</TableCell>
									<TableCell className="px-3 py-2 tabular-nums">
										{(Number(r.priceCents) / 100).toFixed(0)}{" "}
										{r.currency}
									</TableCell>
									<TableCell className="px-3 py-2 tabular-nums text-muted-foreground">
										{Number(r.minOrderCents)
											? `${(Number(r.minOrderCents) / 100).toFixed(0)}`
											: "--"}
									</TableCell>
									<TableCell className="px-3 py-2 tabular-nums text-muted-foreground">
										{r.maxWeightG
											? `${r.maxWeightG} ${t("del_gram")}`
											: "--"}
									</TableCell>
									<TableCell className="px-3 py-2 tabular-nums text-muted-foreground">
										{Number(r.freeAboveCents)
											? `${(Number(r.freeAboveCents) / 100).toFixed(0)}`
											: "--"}
									</TableCell>
									<TableCell className="px-3 py-2 text-right flex gap-1 justify-end">
										<Button
											variant="ghost"
											size="sm"
											className="h-7 w-7 p-0 text-muted-foreground"
											onClick={() => openEdit(r)}
										>
											<Pencil className="h-3.5 w-3.5" />
										</Button>
										<Button
											variant="ghost"
											size="sm"
											className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
											onClick={() => setDeleteTarget(r.id)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			{/* Create / Edit Dialog */}
			<Dialog open={showDialog} onOpenChange={(o) => !o && closeDialog()}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{editId ? t("del_edit_rate") : t("del_new_rate")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						{!editId && (
							<div className="space-y-2">
								<Label>{t("del_col_zone_name")}</Label>
								<Select
									value={form.zoneId}
									onValueChange={(v) => setForm({ ...form, zoneId: v })}
								>
									<SelectTrigger>
										<SelectValue placeholder={t("del_select_zone")} />
									</SelectTrigger>
									<SelectContent>
										{zones.map((z) => (
											<SelectItem key={z.id} value={z.id}>
												{z.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}
						{!editId && (
							<div className="space-y-2">
								<Label>{t("del_col_method_name")}</Label>
								<Input
									value={form.methodName}
									onChange={(e) =>
										setForm({ ...form, methodName: e.target.value })
									}
									placeholder={t("del_method_name_placeholder")}
								/>
							</div>
						)}
						<div className="space-y-2">
							<Label>{t("del_col_price")}</Label>
							<Input
								type="number"
								value={form.priceCents}
								onChange={(e) =>
									setForm({ ...form, priceCents: Number(e.target.value) })
								}
							/>
						</div>
						{!editId && (
							<div className="space-y-2">
								<Label>{t("pay_col_currency")}</Label>
								<Select
									value={form.currency}
									onValueChange={(v) =>
										setForm({ ...form, currency: v })
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="RUB">RUB</SelectItem>
										<SelectItem value="USD">USD</SelectItem>
										<SelectItem value="EUR">EUR</SelectItem>
									</SelectContent>
								</Select>
							</div>
						)}
						<div className="space-y-2">
							<Label>{t("del_col_min_order")}</Label>
							<Input
								type="number"
								value={form.minOrderCents}
								onChange={(e) =>
									setForm({
										...form,
										minOrderCents: Number(e.target.value),
									})
								}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("del_col_max_weight")}</Label>
							<Input
								type="number"
								value={form.maxWeightG}
								onChange={(e) =>
									setForm({
										...form,
										maxWeightG: Number(e.target.value),
									})
								}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("del_col_free_above")}</Label>
							<Input
								type="number"
								value={form.freeAboveCents}
								onChange={(e) =>
									setForm({
										...form,
										freeAboveCents: Number(e.target.value),
									})
								}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeDialog}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() =>
								editId ? updateMut.mutate() : createMut.mutate()
							}
							disabled={
								(!editId && (!form.zoneId || !form.methodName)) ||
								createMut.isPending ||
								updateMut.isPending
							}
						>
							{(createMut.isPending || updateMut.isPending) && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{editId ? t("common:save") : t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Delete Confirm */}
			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("del_delete_rate")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("del_action_irreversible")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteTarget && deleteMut.mutate(deleteTarget)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
